import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Form, Input, List } from 'antd-mobile-v5'
import { WhiteSpace } from 'antd-mobile'
import { FormattedMessage, useIntl } from 'react-intl'
import DetailStatus from '../../components/common/DetailStatus'
import { formatDate, languagesMapping, removeExpires } from '../../components/common/utils'
import { basicMermaidTApproved } from '../../services/api'
import { usePayBasicMermaid } from '../../services/hooks'
import DiverInfo from '../../components/FormTools/DiverInfo'
import Header from '../../components/FormTools/FormHeader'
import UploadOssFile from '../../components/FormTools/UploadOssFile'
import Signature from '../../components/FormTools/Signature'
import SearchInstructor from '../../components/FormTools/SearchInstructor'
import SearchStoreInfo from '../../components/FormTools/SearchStoreInfo'
import DatePickerTool from '../../components/FormTools/DatePicker'
import PickerTool from '../../components/FormTools/Picker'
import AddressForm from '../../components/FormTools/AddressForm'
import dayjs from 'dayjs'

const Item = List.Item

const BasicMermaidTDetail = () => {
  const [form] = Form.useForm()
  const { formatMessage } = useIntl()
  const [signature, setSignature] = useState('')
  const [instructorVerify, setInstructorVerify] = useState(false)
  const [storeVerify, setStoreVerify] = useState(false)
  const { runAsync: toPay } = usePayBasicMermaid()
  const { detail, ossSignature } = useSelector((state) => ({
    detail: state.mermaid.basicMermaidTDetail,
    ossSignature: state.oss.ossSignature,
  }))

  useEffect(() => {
    form.setFieldsValue({
      efri: detail.efri,
      efrfiles: detail.efrfiles || [],
      padiadvancedMermaidFiles: detail.padiadvancedMermaidFiles || [],
      otherFiles: detail.otherFiles || [],
      courseDate: new Date(detail.courseDate) || '',
      instructorNumber: detail.instructorInfoDTO ? detail.instructorInfoDTO.instructorNumber : '',
      storeNumber: detail.storeInfoDTO ? detail.storeInfoDTO.storeNumber : '',
      languageId: detail.languageId || '',
      regionId: detail.regionDTO ? detail.regionDTO.region : '',
      courseLocation: detail.courseLocation || '',
      addressRegionId: detail.addressRegionDTO ? detail.addressRegionDTO.id : '',
      addressStateId: detail.addressStateId || '',
      address: detail.address || '',
    })
    setSignature(detail.handwrittenSignatureUrlTrainer)
  }, [detail])

  const getSubmitData = () => {
    const values = form.getFieldsValue(true)
    const { storeNumber, regionId, courseDate, padiadvancedMermaidFiles, otherFiles, efrfiles, ...data } = values
    let newData = {
      ...data,
      courseDate: formatDate(courseDate, 'YYYY-MM-DD'),
      padiadvancedMermaidFiles: removeExpires(padiadvancedMermaidFiles),
      otherFiles: removeExpires(otherFiles),
      efrfiles: removeExpires(efrfiles),
    }
    if (storeVerify) newData.storeNumber = storeNumber
    return newData
  }

  const editable = !!(detail.status === 'WAIT_CONFIRM' || detail.status === 'ADMIN_NOT_APPROVED')
  const filename = (type) => `${ossSignature.dir}${type}/${new Date().getTime()}`

  if (!Object.keys(detail).length > 0) return null
  return (
    <>
      <Header text={<FormattedMessage id="BMI-app" />} code={detail?.code} />
      <DiverInfo info={detail.userInfoDTO} />
      <Form form={form} validateTrigger="onSubmit">
        <Form.Header>先决条件</Form.Header>
        {detail.efri || detail.efr ? (
          <Item>{`EFR${formatMessage({ id: 'afdi-satisfy' })}`}</Item>
        ) : (
          <Form.Item
            name="efrfiles"
            label={'EFR'}
            form={form}
            rules={[
              {
                required: true,
                message: formatMessage({ id: 'form-upload-error' }),
              },
            ]}
          >
            <UploadOssFile
              form={form}
              fileName={filename('efr')}
              ossSignature={ossSignature}
              onSuccess={(file) => form.setFieldsValue({ efrfiles: file })}
              editable={editable}
              fileList={form.getFieldValue('efrfiles')}
              tips={<FormattedMessage id="common-upload-file-reminder" />}
            />
          </Form.Item>
        )}
        {detail.padiadvancedMermaid ? (
          <Item>{`${formatMessage({ id: 'BMI-advanced-mermaid' })} (${detail.padiadvancedMermaid})`}</Item>
        ) : (
          <Form.Item
            name="padiadvancedMermaidFiles"
            label={formatMessage({ id: 'BMI-advanced-mermaid' })}
            form={form}
            rules={[
              {
                required: true,
                message: formatMessage({ id: 'form-upload-error' }),
              },
            ]}
          >
            <UploadOssFile
              form={form}
              fileName={filename('mermaid')}
              ossSignature={ossSignature}
              onSuccess={(file) => form.setFieldsValue({ padiadvancedMermaidFiles: file })}
              editable={editable}
              fileList={form.getFieldValue('padiadvancedMermaidFiles')}
              tips={<FormattedMessage id="common-upload-file-reminder" />}
            />
          </Form.Item>
        )}
        <Form.Item
          name="otherFiles"
          label={formatMessage({ id: 'mermaid-other-files' })}
          form={form}
          rules={[
            {
              required: true,
              message: formatMessage({ id: 'form-upload-error' }),
            },
          ]}
        >
          <UploadOssFile
            form={form}
            fileName={filename('other')}
            ossSignature={ossSignature}
            onSuccess={(file) => form.setFieldsValue({ otherFiles: file })}
            editable={editable}
            fileList={form.getFieldValue('otherFiles')}
            tips={<FormattedMessage id="app-health-and-others" />}
          />
        </Form.Item>
        <Form.Header>训练信息</Form.Header>
        {(editable || (!editable && detail.instructorInfoDTO)) && (
          <SearchInstructor
            idType={'trainer'}
            type={'NONE'}
            form={form}
            editable={editable}
            instructorVerify={instructorVerify}
            setInstructorVerify={setInstructorVerify}
            required={true}
            initInstructorInfo={detail.instructorInfoDTO}
          />
        )}
        {(editable || (!editable && detail.storeInfoDTO)) && (
          <SearchStoreInfo
            form={form}
            setStoreVerify={setStoreVerify}
            storeVerify={storeVerify}
            editable={editable}
            initStoreInfo={detail.storeInfoDTO}
          />
        )}
        <Form.Item
          form={form}
          name="courseLocation"
          label={formatMessage({ id: 'si-course-location-title' })}
          rules={[
            {
              required: true,
              message: formatMessage({ id: 'si-course-location' }),
            },
          ]}
        >
          <Input />
        </Form.Item>
        <DatePickerTool
          name="courseDate"
          placeholder={formatMessage({ id: 'efri-course-date-error' })}
          disabled={!editable}
          label={formatMessage({ id: 'common-course-date' })}
          rules={[
            {
              required: true,
              message: formatMessage({ id: 'efri-course-date-error' }),
            },
          ]}
          max={dayjs().toDate()}
        />
        <Form.Header>教学地址及语言</Form.Header>
        <Form.Item name="regionId" label={formatMessage({ id: 'dm-region' })} disabled={true}>
          <Input />
        </Form.Item>
        <PickerTool
          name={'languageId'}
          form={form}
          rules={[
            {
              required: true,
              message: '语言不能为空',
            },
          ]}
          placeholder={formatMessage({ id: 'dm-lang-error' })}
          data={languagesMapping}
          label={formatMessage({ id: 'dm-lang-title' })}
          disabled={!editable}
        />
        <Form.Header>地址信息</Form.Header>
        <AddressForm form={form} editable={editable} />
      </Form>
      <WhiteSpace />
      <Signature
        title={formatMessage({ id: 'dm-form-signature' })}
        filePath={'signature'}
        modified={editable}
        oss={ossSignature}
        onSuccess={(imgUrl) => setSignature(imgUrl)}
        onClear={() => setSignature('')}
        initSignature={detail.handwrittenSignatureUrlTrainer}
      />
      <WhiteSpace />
      <DetailStatus
        form={form}
        detail={detail}
        approveFn={basicMermaidTApproved}
        getSubmitData={getSubmitData}
        payFn={toPay}
        listUrl={'/basic_mermaid_app_list'}
        payApplicationType={'MEMBER_APPLICATION_PADI_BASIC_MERMAID_I'}
        signature={signature}
      />
    </>
  )
}

export default BasicMermaidTDetail
